<template>
  <div class="liquidity-add-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <SupplyLiquidity
          ref="supplyLiquidity"
          :pair="pair"
          :pair-data="pairData"
          :transaction="transaction"
          @provideLiquidity="provideLiquidity"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
        />
      </template>
    </WindowModal>
    <section class="liquidity-content">
      <div class="header-area">
        <div class="header-item">
          <nuxt-link class="back-button" :to="localePath('/liquidity')">
            <ArrowBack />
          </nuxt-link>
          <h1>
            {{ $t('liquidity.h1') }}
          </h1>
        </div>
        <div ref="settingsButton" class="settings">
          <div class="toggler" @click="toggleSettings">
            <Cog />
          </div>
        </div>
      </div>
      <div class="subheader-area">
        <div class="h2">
          {{ $t('liquidity.select_pair') }}
        </div>
        <button type="button" class="btn cyan btn-outline" @click="clearForm(true)">
          {{ $t('liquidity.clear_all') }}
        </button>
      </div>
      <div class="tokens-area">
        <div class="token-item">
          <div ref="fromListButton" class="token-select" @click="toggleFromList">
            <div v-if="fromToken === null" class="token-data">
              <div class="token-area">
                <div class="empty-area"></div>
                <p class="normal">
                  {{ $t('liquidity.select_token') }}
                </p>
              </div>
              <div class="chevron-area">
                <ChevronDown />
              </div>
            </div>
            <div v-else class="token-data">
              <div class="token-area">
                <nuxt-img
                  preset="tokenBigLogo"
                  :src="fromToken.address !== $config.ZERO ? $getImageByAddress(fromToken.address) : $getImage(fromToken.logoURI)"
                  :alt="fromToken.name"
                  :title="fromToken.symbol"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
                <p>
                  {{ fromToken.symbol }}
                </p>
              </div>
              <div class="chevron-area">
                <ChevronDown />
              </div>
            </div>
          </div>
          <div ref="fromList" class="token-select-list">
            <div class="h4">
              {{ $t('swap.select_token') }}
            </div>
            <form class="search-area" method="POST">
              <button type="button" class="search-button" :arira-label="$t('common.search')">
                <Search />
              </button>
              <input v-model="search" type="text" :placeholder="$t('common.search') + '...'">
            </form>
            <TokensList :current-token="pair.from_token" :pair-token="pair.to_token" :list="computedList" @pick="selectFromToken" />
            <div ref="manageFromButton" class="open-manage" @click="toggleManageFrom">
              {{ $t('swap.manage') }}
            </div>
            <ManageModal ref="manageFromArea" :active="activeManageFrom" @toggle="toggleManageFrom" />
          </div>
        </div>
        <div class="swap-area">
          <div class="swap-button">
            <Plus />
          </div>
        </div>
        <div :class="{'token-item': true, 'disabled': tokenFixed}">
          <div ref="toListButton" class="token-select" @click="toggleToList()">
            <div v-if="toToken === null" class="token-data">
              <div class="token-area">
                <div class="empty-area"></div>
                <p class="normal">
                  {{ $t('liquidity.select_token') }}
                </p>
              </div>
              <div class="chevron-area">
                <ChevronDown />
              </div>
            </div>
            <div v-else class="token-data">
              <div class="token-area">
                <nuxt-img
                  preset="tokenBigLogo"
                  :src="toToken.address !== $config.ZERO ? $getImageByAddress(toToken.address) : $getImage(toToken.logoURI)"
                  :alt="toToken.name"
                  :title="toToken.symbol"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
                <p>
                  {{ toToken.symbol }}
                </p>
              </div>
              <div class="chevron-area">
                <ChevronDown />
              </div>
            </div>
          </div>
          <div ref="toList" class="token-select-list">
            <div class="h4">
              {{ $t('swap.select_token') }}
            </div>
            <form class="search-area" method="POST">
              <button type="button" class="search-button" :arira-label="$t('common.search')">
                <Search />
              </button>
              <input v-model="search" type="text" :placeholder="$t('common.search') + '...'">
            </form>
            <TokensList :current-token="pair.to_token" :pair-token="pair.from_token" :list="computedList" @pick="selectToToken" />
            <div ref="manageToButton" class="open-manage" @click="toggleManageTo">
              {{ $t('swap.manage') }}
            </div>
            <ManageModal ref="manageToArea" :active="activeManageTo" @toggle="toggleManageTo" />
          </div>
        </div>
      </div>
      <div :class="{ 'information-area': true, 'disabled': !tokensPicked }">
        <div class="price-range-area">
          <div class="h2">
            {{ $t('liquidity.set_price_range') }}
          </div>
          <div class="info-area">
            <div class="text-area">
              <p class="desc">
                {{ $t('liquidity.set_price_range_desc') }}
                <a href="https://medium.com/gamma-strategies/expected-price-range-strategies-in-uniswap-v3-833dff253f84" target="_blank" rel="nofollow noopener norefferer">
                  {{ $t('liquidity.set_price_range_link') }}
                </a>
              </p>
            </div>
            <div class="range-switch-area">
              <div :class="{'switch-item': true, 'active': priceSettings === 'from'}" @click="selectPriceSettings('from')">
                <span>
                  {{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}
                </span>&nbsp;{{ $t('common.price') }}
              </div>
              <div :class="{'switch-item': true, 'active': priceSettings === 'to'}" @click="selectPriceSettings('to')">
                <span>
                  {{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}
                </span>&nbsp;{{ $t('common.price') }}
              </div>
            </div>
          </div>
        </div>
        <div class="prices-area">
          <div v-if="priceSettings === 'from'" ref="fromPriceSettings" class="positions">
            <div class="position-area">
              <div class="h2">
                {{ $t('liquidity.min_price') }}
              </div>
              <p class="subheader">
                <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
                {{ $t('common.per') }}
                <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
              </p>
              <div class="position">
                <div class="number-area">
                  <p class="percent">
                    {{ liquidityMinFromValue }}
                  </p>
                </div>
                <div class="buttons-area">
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.from_value === null || pair.from_value <= 0 || liquidity.min_price_from <= 0
                    }"
                    @click="minusMinFromPercnet"
                  >
                    -{{ liquidity.fee }}%
                  </button>
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.from_value === null || pair.from_value <= 0,
                    }"
                    @click="addMinFromPercnet"
                  >
                    +{{ liquidity.fee }}%
                  </button>
                </div>
              </div>
            </div>
            <div class="position-area">
              <div class="h2">
                {{ $t('liquidity.max_price') }}
              </div>
              <p class="subheader">
                <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
                {{ $t('common.per') }}
                <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
              </p>
              <div class="position">
                <div class="number-area">
                  <p class="percent">
                    {{ liquidityMaxFromValue }}
                  </p>
                </div>
                <div class="buttons-area">
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.from_value === null || pair.from_value <= 0 || liquidity.min_price_from <= 0
                    }"
                    @click="minusMaxFromPercnet"
                  >
                    -{{ liquidity.fee }}%
                  </button>
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.from_value === null || pair.from_value <= 0,
                    }"
                    @click="addMaxFromPercnet"
                  >
                    +{{ liquidity.fee }}%
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="priceSettings === 'to'" ref="toPriceSettings" class="positions">
            <div class="position-area">
              <div class="h2">
                {{ $t('liquidity.min_price') }}
              </div>
              <p class="subheader">
                <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
                {{ $t('common.per') }}
                <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
              </p>
              <div class="position">
                <div class="number-area">
                  <p class="percent">
                    {{ liquidityMinToValue }}
                  </p>
                </div>
                <div class="buttons-area">
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.to_value === null || pair.to_value <= 0 || liquidity.min_price_to <= 0,
                    }"
                    @click="minusMinToPercnet"
                  >
                    -{{ liquidity.fee }}%
                  </button>
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.to_value === null || pair.to_value <= 0,
                    }"
                    @click="addMinToPercnet"
                  >
                    +{{ liquidity.fee }}%
                  </button>
                </div>
              </div>
            </div>
            <div class="position-area">
              <div class="h2">
                {{ $t('liquidity.max_price') }}
              </div>
              <p class="subheader">
                <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
                {{ $t('common.per') }}
                <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
              </p>
              <div class="position">
                <div class="number-area">
                  <p class="percent">
                    {{ liquidityMaxToValue }}
                  </p>
                </div>
                <div class="buttons-area">
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.to_value === null || pair.to_value <= 0 || liquidity.min_price_to <= 0,
                    }"
                    @click="minusMaxToPercnet"
                  >
                    -{{ liquidity.fee }}%
                  </button>
                  <button
                    type="button"
                    :class="{ 'btn cyan btn-outline': true,
                              'disabled': pair.to_value === null || pair.to_value <= 0,
                    }"
                    @click="addMaxToPercnet"
                  >
                    +{{ liquidity.fee }}%
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="h2">
          {{ $t('liquidity.deposit_amounts') }}
        </div>
        <div class="deposited-tokens">
          <div class="token-area">
            <div v-if="pair.from_token.symbol.length === 0" class="token-data">
              <div class="token">
                <div class="empty-area"></div>
                <p>
                  {{ $t('liquidity.select_token') }}
                </p>
              </div>
            </div>
            <div v-else class="token-data">
              <div class="token">
                <nuxt-img
                  preset="tokenBigLogo"
                  :src="fromToken.address !== $config.ZERO ? $getImageByAddress(fromToken.address) : $getImage(fromToken.logoURI)"
                  :alt="fromToken.name"
                  :title="fromToken.symbol"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
                <p>
                  {{ fromToken.symbol }}
                </p>
              </div>
              <div :class="{'copy-area': true, 'active': fromToken.address.length > 0 && fromToken.address !== $config.ZERO}">
                <div class="svg-area" :tooltip="copyText" @click="copyAddress($event, fromToken.address)">
                  <Copy />
                </div>
                <div class="svg-area" :tooltip="$t('common.add_to_metamask')" @click="$addToMetamask(fromToken)">
                  <MetamaskIcon />
                </div>
              </div>
            </div>
            <div class="token-price">
              <input
                v-model="pair.from_value"
                class="amount-input"
                type="number"
                step="0.000001"
                min="0"
                lang="en"
                @input="inputFrom"
              >
              <div v-if="pair.from_value_usd !== undefined && pair.from_value_usd !== 0" class="usd-price">
                &asymp; ${{ $priceFormat(pair.from_value_usd * parseFloat(pair.from_value), 2) }}
              </div>
              <p v-if="fromToken !== null && fromToken.address.length > 0" class="available">
                {{ $t('swap.available') }}:
                <span v-if="fromToken.balance !== undefined && fromToken.balance !== null" class="bold">{{ $formatCryptoAmount(parseFloat(fromToken.balance), 4) }}</span>
                <span v-else class="bold">0</span>
              </p>
              <div :class="{'max-button': true, 'active': fromToken !== null && fromToken.address.length > 0}" @click="setFromMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
          </div>
          <div class="swap-area">
            <div class="swap-button">
              <Plus />
            </div>
          </div>
          <div class="token-area">
            <div v-if="pair.to_token.symbol.length === 0" class="token-data">
              <div class="token">
                <div class="empty-area"></div>
                <p>
                  {{ $t('liquidity.select_token') }}
                </p>
              </div>
            </div>
            <div v-else class="token-data">
              <div class="token">
                <nuxt-img
                  preset="tokenBigLogo"
                  :src="toToken.address !== $config.ZERO ? $getImageByAddress(toToken.address) : $getImage(toToken.logoURI)"
                  :alt="toToken.name"
                  :title="toToken.symbol"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
                <p>
                  {{ toToken.symbol }}
                </p>
              </div>
              <div :class="{'copy-area': true, 'active': toToken.address.length > 0 && toToken.address !== $config.ZERO}">
                <div class="svg-area" :tooltip="copyText" @click="copyAddress($event, toToken.address)">
                  <Copy />
                </div>
                <div class="svg-area" :tooltip="$t('common.add_to_metamask')" @click="$addToMetamask(toToken)">
                  <MetamaskIcon />
                </div>
              </div>
            </div>
            <div class="token-price">
              <input
                v-model="pair.to_value"
                class="amount-input"
                type="number"
                step="0.000001"
                min="0"
                lang="en"
                @input="inputTo"
              >
              <div v-if="pair.to_value_usd !== undefined && pair.to_value_usd !== 0" class="usd-price">
                &asymp; ${{ $priceFormat(pair.to_value_usd * parseFloat(pair.to_value), 2) }}
              </div>
              <p v-if="toToken !== null && toToken.address.length > 0" class="available">
                {{ $t('swap.available') }}:
                <span v-if="toToken.balance !== undefined && toToken.balance !== null" class="bold">{{ $formatCryptoAmount(parseFloat(toToken.balance), 4) }}</span>
                <span v-else class="bold">0</span>
              </p>
              <div :class="{'max-button': true, 'active': toToken !== null && toToken.address.length > 0}" @click="setToMax">
                <p class="text">
                  {{ $t('swap.max') }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div :class="{'pair-info': true, 'mb-0': errorText.length > 0}">
          <div class="info-item">
            <div class="percent">
              {{ pairData !== null ? $formatCryptoAmount(pairData.reserves.reserve0 / pairData.reserves.reserve1, 6) : $t('liquidity.tbd') }}
            </div>
            <div class="tokens">
              <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
              {{ $t('common.per') }}
              <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
            </div>
          </div>
          <div class="info-item">
            <div class="percent">
              {{ pairData !== null ? $formatCryptoAmount(pairData.reserves.reserve1 / pairData.reserves.reserve0, 6) : $t('liquidity.tbd') }}
            </div>
            <div class="tokens">
              <span>{{ pair.to_token !== null ? pair.to_token.symbol.toUpperCase() : '...' }}</span>
              {{ $t('common.per') }}
              <span>{{ pair.from_token !== null ? pair.from_token.symbol.toUpperCase() : '...' }}</span>
            </div>
          </div>
          <div class="info-item">
            <div class="percent">
              {{ pairData !== null ? $priceFormat(calculatedPoolShare, 2, '.', ',') + '%' : $t('liquidity.tbd') }}
            </div>
            <div class="tokens">
              {{ $t('liquidity.share_of_pool') }}
            </div>
          </div>
        </div>
        <div :class="{'warning-area': true, 'active': errorText.length > 0}">
          <div class="icon-area">
            <Warn />
          </div>
          <div class="text-area">
            {{ errorText }}
          </div>
        </div>
        <div class="buttons-area">
          <button v-if="user !== undefined && user !== null" :class="{ 'btn cyan': true, 'disabled': !liquidityPicked || lowBalances || supplyCtaDisabled || loading }" type="button" :disabled="!liquidityPicked || lowBalances || supplyCtaDisabled || loading" @click="prepareLiquidity">
            <span v-if="!loading">
              {{ $t('liquidity.cta') }}
            </span>
            <span v-else>
              <Loading />
            </span>
          </button>
          <button v-else class="btn cyan" @click="openConnectModal">
            {{ $t('common.connect_wallet') }}
          </button>
        </div>
      </div>
    </section>
    <div class="cyan-cloud">
      <CloudCyanLiquidityLeft />
    </div>
    <div class="cyan-cloud2">
      <CloudCyanLiquidityRight />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import ManageModal from '~/components/modals/ManageModal'
import SupplyLiquidity from '~/components/app/interface/SupplyLiquidity'
import ArrowBack from '~/components/svg/new/ArrowBack'
import Plus from '~/components/svg/new/Plus'

export default {
  name: 'LiquidityAddComponent',
  components: {
    ArrowBack,
    ChevronDown: hydrateWhenIdle(() => import('~/components/svg/new/ChevronDown')),
    CloudCyanLiquidityLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityLeft')),
    CloudCyanLiquidityRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityRight')),
    Cog: hydrateWhenIdle(() => import('~/components/svg/new/Cog')),
    Copy: hydrateWhenVisible(() => import('~/components/svg/new/Copy')),
    Plus,
    Loading: hydrateWhenVisible(() => import('~/components/svg/new/Loading')),
    ManageModal,
    MetamaskIcon: hydrateWhenVisible(() => import('~/components/svg/MetamaskIcon')),
    Search: hydrateWhenVisible(() => import('~/components/svg/new/Search')),
    SupplyLiquidity,
    Warn: hydrateWhenVisible(() => import('~/components/svg/new/Warn')),
    TokensList: hydrateWhenIdle(() => import('~/components/app/interface/TokensList')),
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      search: '',
      importManage: '',
      errorText: '',
      copyText: this.$t('common.copy'),
      tokenFixed: false,
      feeOpen: false,
      activeManageFrom: false,
      activeManageTo: false,
      priceSettings: 'from',
      pair: {
        from_value: 0,
        from_value_usd: 0,
        from_token: {
          symbol: '',
          address: '',
          decimals: 18,
          balance: 0
        },
        to_value: 0,
        to_value_usd: 0,
        to_token: {
          symbol: '',
          address: '',
          decimals: 18,
          balance: 0
        }
      },
      pairData: null,
      walletConnected: false,
      modalTitle: this.$t('liquidity.you_will_receive'),
      feeList: [0.05, 0.3, 1],
      loadQueryAttempts: 0,
      liquidity: {
        fee: 0.3,
        current_price: 0,
        min_price_from: 0,
        max_price_from: 0,
        min_price_to: 0,
        max_price_to: 0
      },
      transaction: {
        gasFee: 3000000,
        tx: '',
        loading: false,
        pending: false
      },
      supplyCtaDisabled: false,
      createPairFirst: false,
      loading: false
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('settings', [
      'slippageTolerance',
      'deadline',
      'multihops',
      'speed',
      'expertMode'
    ]),
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    },
    fromToken() {
      const token = this.$store.getters['tokens/list'].length > 0 && this.pair.from_token.symbol.length > 0
        ? this.$store.getters['tokens/list'].find(item => item.symbol.toLowerCase() === this.pair.from_token.symbol.toLowerCase())
        : null
      return token !== undefined ? token : this.$store.getters['tokens/list'][0]
    },
    toToken() {
      const token = this.$store.getters['tokens/list'].length > 1 && this.pair.to_token.symbol.length > 0
        ? this.$store.getters['tokens/list'].find(item => item.symbol.toLowerCase() === this.pair.to_token.symbol.toLowerCase())
        : null
      return token !== undefined ? token : this.$store.getters['tokens/list'][1]
    },
    tokensPicked() {
      return this.pair.from_token.address.length > 0 && this.pair.to_token.address.length > 0
    },
    liquidityPicked() {
      return this.pair.from_token.address.length > 0 && this.pair.to_token.address.length > 0 &&
        this.pair.from_value !== null && this.pair.to_value !== null && this.walletConnected &&
        this.pair.from_value > 0 && this.pair.to_value > 0
    },
    lowBalances() {
      return (this.fromToken.balance !== undefined && this.fromToken.balance !== null && parseFloat(this.fromToken.balance) < this.pair.from_value) ||
        (this.toToken.balance !== undefined && this.toToken.balance !== null && parseFloat(this.toToken.balance) < this.pair.to_value)
    },
    computedList() {
      return this.$store.getters['tokens/list'].filter(item => item.name.toLowerCase().includes(this.search.toLowerCase()) || item.symbol.toLowerCase().includes(this.search.toLowerCase()))
    },
    liquidityMinFromValue() {
      if (this.pair.from_value === 0 || this.pair.from_value === null) {
        return 0
      }
      const price = this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
        this.pair.from_value / 100 * this.slippageTolerance
      return this.$formatCryptoAmount(price, 6)
    },
    liquidityMaxFromValue() {
      if (this.pair.from_value === 0 || this.pair.from_value === null) {
        return 0
      }
      return this.$formatCryptoAmount(parseFloat(this.pair.from_value), 6)
    },
    liquidityMinToValue() {
      if (this.pair.to_value === 0 || this.pair.to_value === null) {
        return 0
      }
      const price = this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
        this.pair.to_value / 100 * this.slippageTolerance
      return this.$formatCryptoAmount(price, 6)
    },
    liquidityMaxToValue() {
      if (this.pair.to_value === 0 || this.pair.to_value === null) {
        return 0
      }
      return this.$formatCryptoAmount(parseFloat(this.pair.to_value), 6)
    },
    calculatedPoolShare() {
      if (this.pairData !== null) {
        let fromValue = 0
        if (this.pair.from_value) {
          fromValue = parseFloat(this.pair.from_value)
        }
        return fromValue / (this.pairData.reserves.reserve0 + fromValue) * 100
      } else {
        return 0
      }
    }
  },
  mounted() {
    if (this.user !== null) {
      this.walletConnected = true
    }
    window.addEventListener('click', this.toggleModals)
    this.$nuxt.$on('tokensLoaded', this.loadQueryTokens)
    if (this.$refs.supplyLiquidity !== undefined && (this.$route.query.from !== undefined || this.$route.query.to !== undefined)) {
      this.$refs.supplyLiquidity.initLoadingScreen(this.$t('liquidity.loading_header'), this.$t('liquidity.loading_text'))
      this.changeModalGlobalState({
        state: true
      })
    }
    this.$nuxt.$emit('tokenListToggle')
    if (this.computedList.length > 1) {
      this.loadQueryTokens()
    }
  },
  beforeDestroy() {
    window.removeEventListener('click', this.toggleModals)
  },
  methods: {
    ...mapActions('modal', [
      'changeModalSettingsState',
      'changeModalGlobalState',
      'changeModalWalletState'
    ]),
    ...mapActions('tokens', [
      'addToken'
    ]),
    ...mapMutations('tokens', [
      'changeBalance'
    ]),
    ...mapActions('pairs', [
      'updatePairShare'
    ]),
    openConnectModal() {
      this.changeModalWalletState({
        state: true
      })
    },
    toggleSettings() {
      this.changeModalSettingsState({
        state: true
      })
    },
    loadQueryTokens() {
      if (this.computedList.length <= 1 && this.loadQueryAttempts < 10) {
        this.loadQueryAttempts += 1
        setTimeout(() => {
          this.loadQueryTokens()
        }, 500)
      }
      if (this.loadQueryAttempts >= 10) {
        return
      }
      const fromAddress = this.$route.query.from
      const toAddress = this.$route.query.to
      const toFixed = this.$route.query.toStatus
      if (fromAddress !== undefined && fromAddress !== null) {
        const tokenFrom = this.computedList.find(tkn => tkn.address === fromAddress)
        if (tokenFrom === undefined || tokenFrom === null) {
          this.importAndSelectToken(fromAddress, 'from')
        } else {
          this.selectFromToken(tokenFrom, false)
        }
      }
      if (toAddress !== undefined && toAddress !== null) {
        const tokenTo = this.computedList.find(tkn => tkn.address === toAddress)
        if (tokenTo === undefined || tokenTo === null) {
          this.importAndSelectToken(toAddress, 'to')
        } else {
          this.selectToToken(tokenTo, false)
        }
      }
      if (toFixed !== undefined && toFixed !== null && toAddress !== undefined && toAddress !== null) {
        this.tokenFixed = true
      }
      window.scrollTo(0, 0)
      if (this.$refs.supplyLiquidity !== undefined) {
        this.changeModalGlobalState({
          state: false
        })
        this.$refs.supplyLiquidity.returnInitState()
      }
    },
    toggleFromList() {
      if (this.$refs.fromList !== undefined) {
        this.$refs.fromList.classList.toggle('active')
        this.$refs.fromListButton.classList.toggle('active')
        this.search = ''
      }
    },
    async selectFromToken(token, closeWindow = true) {
      const userAddress = await this.$getUserAddress()
      if (this.pair.to_token.symbol.toLowerCase() !== token.symbol.toLowerCase()) {
        this.pair.from_token = token
        this.pairData = null
        if (closeWindow) {
          this.toggleFromList()
        }
        try {
          this.pair.from_value_usd = await this.$loadTokenPrice(this.pair.from_token.address)
        } catch (err) {
          this.pair.from_value_usd = 0
        }
        try {
          this.createPairFirst = false
          this.loading = true
          const ERC20file = await import('~/assets/abis/ERC20.json')
          const ERC20 = ERC20file.default
          const tokenContract = this.pair.from_token.address !== this.$config.ZERO ? new window.eth.eth.Contract(ERC20, this.pair.from_token.address) : null
          if (tokenContract !== null) {
            const balanceFrom = await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })
            const decimals = await tokenContract.methods.decimals().call({ from: userAddress })
            // this.pair.from_token.decimals = parseInt(decimals)
            const currentDecimal = this.$getDecimalsName(decimals)
            let balance = parseFloat(window.eth.utils.fromWei(balanceFrom, currentDecimal.name))
            if (currentDecimal.multiply > 0) {
              balance /= 10 ** currentDecimal.multiply
            }
            this.changeBalance({
              address: this.pair.from_token.address,
              balance
            })
          } else {
            this.changeBalance({
              address: this.pair.from_token.address,
              balance: parseFloat(window.eth.utils.fromWei(await window.eth.eth.getBalance(userAddress)))
            })
          }
          this.pairData = await this.$loadPairData(this.pair.from_token, this.pair.to_token)
          this.loading = false
          if (this.pairData !== null) {
            this.calculatePrices(this.priceSettings)
          }
        } catch (err) {
          this.loading = false
          this.pairIsNotProvided()
        }
      }
    },
    toggleManageFrom() {
      this.activeManageFrom = !this.activeManageFrom
      this.importManage = ''
    },
    toggleToList() {
      if (this.$refs.toList !== undefined) {
        this.$refs.toList.classList.toggle('active')
        this.$refs.toListButton.classList.toggle('active')
        this.search = ''
      }
    },
    async selectToToken(token, closeWindow = true) {
      const userAddress = await this.$getUserAddress()
      if (this.pair.from_token.symbol.toLowerCase() !== token.symbol.toLowerCase()) {
        this.pair.to_token = token
        this.pairData = null
        if (closeWindow) {
          this.toggleToList()
        }
        try {
          this.pair.to_value_usd = await this.$loadTokenPrice(this.pair.to_token.address)
        } catch (err) {
          this.pair.to_value_usd = 0
        }
        try {
          this.createPairFirst = false
          this.loading = true
          const ERC20file = await import('~/assets/abis/ERC20.json')
          const ERC20 = ERC20file.default
          const tokenContract = this.pair.to_token.address !== this.$config.ZERO ? new window.eth.eth.Contract(ERC20, this.pair.to_token.address) : null
          if (tokenContract !== null) {
            const balanceFrom = await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress })
            const decimals = await tokenContract.methods.decimals().call({ from: userAddress })
            // this.pair.to_token.decimals = parseInt(decimals)
            const currentDecimal = this.$getDecimalsName(decimals)
            let balance = parseFloat(window.eth.utils.fromWei(balanceFrom, currentDecimal.name))
            if (currentDecimal.multiply > 0) {
              balance /= 10 ** currentDecimal.multiply
            }
            this.changeBalance({
              address: this.pair.to_token.address,
              balance
            })
          } else {
            this.changeBalance({
              address: this.pair.to_token.address,
              balance: parseFloat(window.eth.utils.fromWei(await window.eth.eth.getBalance(userAddress)))
            })
          }
          this.pairData = await this.$loadPairData(this.pair.from_token, this.pair.to_token)
          this.loading = false
          if (this.pairData !== null) {
            this.calculatePrices(this.priceSettings)
          }
        } catch (err) {
          this.loading = false
          this.pairIsNotProvided()
        }
      }
    },
    toggleManageTo() {
      this.activeManageTo = !this.activeManageTo
      this.importManage = ''
    },
    setFee(percent) {
      this.liquidity.fee = percent
      this.toggleFeeArea()
    },
    toggleFeeArea() {
      this.feeOpen = !this.feeOpen
      const heightElem = this.$refs.feeArea.querySelector('.items')
      if (this.feeOpen) {
        this.$slideDown(this.$refs.feeArea, heightElem)
      } else {
        this.$slideUp(this.$refs.feeArea)
      }
    },
    clearForm(withMessage = false) {
      this.search = ''
      this.importManage = ''
      this.feeOpen = false
      if (!this.tokenFixed) {
        this.pair = {
          from_value: null,
          from_value_usd: null,
          from_token: {
            symbol: '',
            address: '',
            decimals: 18
          },
          to_value: null,
          to_value_usd: null,
          to_token: {
            symbol: '',
            address: '',
            decimals: 18
          }
        }
      } else {
        this.pair.from_value = null
        this.pair.from_value_usd = null
        this.pair.to_value = null
        this.pair.to_value_usd = null
        this.pair.from_token = {
          symbol: '',
          address: '',
          decimals: 18
        }
      }
      this.settings = {
        slippage_tolerance: 1,
        deadline: 30,
        expert_mode: false,
        multihops: false
      }
      this.liquidity = {
        fee: 0,
        current_price: 0,
        min_price_from: 0,
        max_price_from: 0,
        min_price_to: 0,
        max_price_to: 0
      }
      if (withMessage) {
        console.log(this.$t('liquidity.form_cleared'))
      }
    },
    pairIsNotProvided() {
      this.createPairFirst = true
      this.$nuxt.$emit('openInfo', this.$t('liquidity.pair_is_not_present'), this.$t('liquidity.pair_is_not_present_text'))
    },
    selectPriceSettings(tokenSide) {
      this.priceSettings = tokenSide
      if (this.pairData !== null) {
        this.calculatePrices(this.priceSettings)
      }
    },
    isTokenActive(symbol) {
      return this.pair.from_token.symbol.toLowerCase() === symbol.toLowerCase() ||
        this.pair.to_token.symbol.toLowerCase() === symbol.toLowerCase()
    },
    inputFrom() {
      this.checkMinLiquidity()
      this.liquidity.max_price_from = this.pair.from_value
      if (this.pairData !== null && this.pairData.constantProduct !== 0) {
        const toTokenPrice = this.pairData.reserves.reserve1 / this.pairData.reserves.reserve0 * this.pair.from_value
        this.pair.to_value = this.$formatCryptoAmount(toTokenPrice, 10, true)
      }
      if (this.pair.to_value > this.pair.to_token.balance) {
        this.pair.to_value = this.pair.to_token.balance
      }
    },
    inputTo() {
      this.checkMinLiquidity()
      this.liquidity.max_price_to = this.pair.to_value
      if (this.pairData !== null && this.pairData.constantProduct !== 0) {
        const fromTokenPrice = this.pairData.reserves.reserve0 / this.pairData.reserves.reserve1 * this.pair.to_value
        this.pair.from_value = this.$formatCryptoAmount(fromTokenPrice, 10, true)
      }
      if (this.pair.from_value > this.pair.from_token.balance) {
        this.pair.from_value = this.pair.from_token.balance
      }
    },
    setFromMax() {
      this.pair.from_value = this.pair.from_token.balance
      this.inputFrom()
    },
    setToMax() {
      this.pair.to_value = this.pair.to_token.balance
      this.inputTo()
    },
    calculatePrices(tokenSide) {
      if (this.pairData.constantProduct > 0) {
        const fromTokenPrice = this.pairData.reserves.reserve1 / this.pairData.reserves.reserve0
        const toTokenPrice = this.pairData.reserves.reserve0 / this.pairData.reserves.reserve1

        if (tokenSide === 'from') {
          this.liquidity.current_price = fromTokenPrice
        } else {
          this.liquidity.current_price = toTokenPrice
        }
      } else {
        this.liquidity.current_price = 0

        this.liquidity.min_price_from = 0
        this.liquidity.max_price_from = 0
        this.liquidity.min_price_to = 0
        this.liquidity.max_price_to = 0
      }
    },
    checkMinLiquidity() {
      if (this.pairData === null && this.pair.from_value * this.pair.to_value - 1000 <= 0) {
        this.errorText = this.$t('liquidity.min_liquidity')
        this.supplyCtaDisabled = true
      } else {
        this.normalizeCta()
      }
    },
    async prepareLiquidity() {
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        return
      }
      this.changeModalGlobalState({
        state: true
      })
    },
    minusMinFromPercnet() {
      this.liquidity.min_price_from -= this.liquidity.fee
      if (this.liquidity.min_price_from <= 0) {
        this.liquidity.min_price_from = 0
      }
    },
    addMinFromPercnet() {
      this.liquidity.min_price_from += this.liquidity.fee
      if (this.liquidityMinFromValue <= 0) {
        this.liquidity.min_price_from -= this.liquidity.fee
      }
    },
    minusMinToPercnet() {
      this.liquidity.min_price_to -= this.liquidity.fee
      if (this.liquidityMinToValue >= this.pair.to_value) {
        this.liquidity.min_price_to += this.liquidity.fee
      }
      if (this.liquidity.min_price_to <= 0) {
        this.liquidity.min_price_to = 0
      }
    },
    addMinToPercnet() {
      this.liquidity.min_price_to += this.liquidity.fee
      if (this.liquidityMinToValue <= 0) {
        this.liquidity.min_price_to -= this.liquidity.fee
      }
    },
    minusMaxFromPercnet() {
      this.liquidity.max_price_from -= this.liquidity.fee
      if (this.liquidityMaxFromValue >= this.liquidityMaxFromValue * 2) {
        this.liquidity.max_price_from += this.liquidity.fee
      }
      if (this.liquidity.max_price_from <= 0) {
        this.liquidity.max_price_from = 0
      }
    },
    addMaxFromPercnet() {
      this.liquidity.max_price_from += this.liquidity.fee
      if (this.liquidityMaxFromValue <= this.pair.from_value) {
        this.liquidity.max_price_from -= this.liquidity.fee
      }
    },
    minusMaxToPercnet() {
      this.liquidity.max_price_to -= this.liquidity.fee
      if (this.liquidityMaxToValue >= this.liquidityMaxToValue * 2) {
        this.liquidity.max_price_to += this.liquidity.fee
      }
      if (this.liquidity.max_price_to <= 0) {
        this.liquidity.max_price_to = 0
      }
    },
    addMaxToPercnet() {
      this.liquidity.max_price_to += this.liquidity.fee
      if (this.liquidityMaxToValue <= this.pair.to_value) {
        this.liquidity.max_price_to -= this.liquidity.fee
      }
    },
    normalizeCta() {
      this.errorText = ''
      this.supplyCtaDisabled = false
    },
    async provideLiquidity() {
      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      if (this.createPairFirst) {
        const pairCreated = await this.createPair()
        if (!pairCreated) {
          this.transaction.loading = false
          this.transaction.pending = false
          return
        }
      }
      const userAddress = await this.$getUserAddress()
      const PeriodRouterFile = await import('~/assets/abis/PeriodRouter.json')
      const PeriodRouter = PeriodRouterFile.default
      const routerContract = new window.eth.eth.Contract(PeriodRouter, this.$config.periodRouter)
      const allowances = []
      let breakWithAllowance = false
      let tokenToSave = this.pair.from_token
      if (this.$isStableCurrency(this.pair.from_token.symbol) || this.$isStableCurrency(this.pair.to_token.symbol)) {
        let nonStableToken = 'from'
        if (!this.$isStableCurrency(this.pair.from_token.symbol)) {
          const allowanceData = await this.checkAllowance(this.pair.from_token.address, this.pair.from_value)
          if (!allowanceData) {
            return
          }
          allowances.push(allowanceData)
        } else {
          const allowanceData = await this.checkAllowance(this.pair.to_token.address, this.pair.to_value)
          if (!allowanceData) {
            return
          }
          nonStableToken = 'to'
          tokenToSave = this.pair.to_token
          allowances.push(allowanceData)
        }
        allowances.forEach((allowance) => {
          if (allowance === false) {
            breakWithAllowance = true
          }
        })
        if (!breakWithAllowance) {
          try {
            if (isNaN(Number(this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
                this.pair.from_value / 100 * this.slippageTolerance)) || isNaN(Number(this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
                this.pair.to_value / 100 * this.slippageTolerance))) {
              if (this.$refs.supplyLiquidity !== undefined) {
                this.changeModalGlobalState({
                  state: true
                })
                this.$refs.supplyLiquidity.rejectTransaction({
                  type: 'error',
                  message: this.$t('liquidity.input_nan')
                })
              }
              return
            }
            const parameters = await this.addLiquidityETHParameters(nonStableToken)
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await routerContract.methods
              .addLiquidityETH(
                parameters.token,
                parameters.amountTokenDesired,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline
              ).estimateGas({ from: userAddress, value: parameters.amountIn, gas: this.transaction.gasFee })
            await routerContract.methods
              .addLiquidityETH(
                parameters.token,
                parameters.amountTokenDesired,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline
              ).send({ from: userAddress, value: parameters.amountIn, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then(async (result) => {
                this.transaction.loading = false
                this.transaction.pending = false
                this.transaction.tx = result.transactionHash
                const PeriodFactoryFile = await import('~/assets/abis/PeriodFactory.json')
                const PeriodFactory = PeriodFactoryFile.default
                const factoryContract = new window.eth.eth.Contract(PeriodFactory, this.$config.periodFactory)
                const pairAddress = await factoryContract.methods.getPair(tokenToSave.address, this.$config.wbnbAddress).call({ from: userAddress })
                if (tokenToSave.address.toLowerCase() === this.pair.from_token.address.toLowerCase()) {
                  this.$savePair(tokenToSave, {
                    chainId: this.$config.chainId,
                    address: this.$config.wbnbAddress,
                    decimals: 18,
                    symbol: 'WBNB',
                    name: 'WBNB'
                  }, pairAddress)
                } else {
                  this.$savePair({
                    chainId: this.$config.chainId,
                    address: this.$config.wbnbAddress,
                    decimals: 18,
                    symbol: 'WBNB',
                    name: 'WBNB'
                  }, tokenToSave, pairAddress)
                }
                this.$saveTransaction({
                  tid0: this.pair.from_token.address,
                  tid1: this.pair.to_token.address,
                  tid0_token: this.pair.from_token,
                  tid1_token: this.pair.to_token,
                  t0_amount: this.pair.from_value,
                  t1_amount: this.pair.to_value,
                  hash: result.transactionHash,
                  gasUsed: result.gasUsed,
                  transactionIndex: result.transactionIndex,
                  type: 'add'
                },
                this.$t('common.supply_action') + ' ' + this.$formatCryptoAmount(parseFloat(this.pair.from_value), 4) + ' ' + this.pair.from_token.symbol +
                  ' ' + this.$t('common.and') + ' ' + this.$formatCryptoAmount(parseFloat(this.pair.to_value), 4) + ' ' + this.pair.to_token.symbol
                )
                await this.updatePoolShare(pairAddress)
                this.clearForm(false)
                if (this.$refs.supplyLiquidity !== undefined) {
                  this.changeModalGlobalState({
                    state: true
                  })
                  this.$refs.supplyLiquidity.initSuccessScreen()
                }
              })
              .catch((err) => {
                this.transaction.loading = false
                this.transaction.pending = false
                if (err.code === 4001) {
                  if (this.$refs.supplyLiquidity !== undefined) {
                    this.changeModalGlobalState({
                      state: true
                    })
                    this.$refs.supplyLiquidity.rejectTransaction({
                      type: 'denied'
                    })
                  }
                } else {
                  const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
                  if (this.$refs.supplyLiquidity !== undefined) {
                    this.changeModalGlobalState({
                      state: true
                    })
                    this.$refs.supplyLiquidity.rejectTransaction({
                      type: 'error',
                      message: errorMessage
                    })
                  }
                }
              })
          } catch (err) {
            this.transaction.loading = false
            this.transaction.pending = false
            let errorMessage = this.$t('liquidity.error_text')
            if (err.message !== undefined && err.message !== null) {
              errorMessage = this.$t('liquidity.error_text_with_message') + ': ' + this.$getRPCError(err.message)
            }
            if (this.$refs.supplyLiquidity !== undefined) {
              this.changeModalGlobalState({
                state: true
              })
              this.$refs.supplyLiquidity.rejectTransaction({
                type: 'error',
                message: errorMessage
              })
            }
          }
        } else {
          this.transaction.loading = false
          this.transaction.pending = false
        }
      } else {
        let allowanceData = await this.checkAllowance(this.pair.from_token.address, this.pair.from_value)
        if (!allowanceData) {
          return
        }
        allowances.push(allowanceData)
        allowanceData = await this.checkAllowance(this.pair.to_token.address, this.pair.to_value)
        if (!allowanceData) {
          return
        }
        allowances.push(allowanceData)
        allowances.forEach((allowance) => {
          if (allowance === false) {
            breakWithAllowance = true
          }
        })
        if (!breakWithAllowance) {
          if (isNaN(Number(this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
              this.pair.from_value / 100 * this.slippageTolerance)) || isNaN(Number(this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
              this.pair.to_value / 100 * this.slippageTolerance))) {
            if (this.$refs.supplyLiquidity !== undefined) {
              this.changeModalGlobalState({
                state: true
              })
              this.$refs.supplyLiquidity.rejectTransaction({
                type: 'error',
                message: this.$t('liquidity.input_nan')
              })
            }
            return
          }
          const parameters = {
            tokenA: this.pair.from_token.address,
            tokenB: this.pair.to_token.address,
            amountADesired: this.$toWeiDecimal(Number(this.pair.from_value).toPrecision(10), this.pair.from_token.decimals),
            amountBDesired: this.$toWeiDecimal(Number(this.pair.to_value).toPrecision(10), this.pair.to_token.decimals),
            amountAMin: this.$toWeiDecimal(Number(this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
              this.pair.from_value / 100 * this.slippageTolerance).toPrecision(10), this.pair.from_token.decimals),
            amountBMin: this.$toWeiDecimal(Number(this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
              this.pair.to_value / 100 * this.slippageTolerance).toPrecision(10), this.pair.to_token.decimals),
            to: userAddress,
            deadline: this.$calculateDeadline(this.deadline)
          }
          try {
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await routerContract.methods
              .addLiquidity(
                parameters.tokenA,
                parameters.tokenB,
                parameters.amountADesired,
                parameters.amountBDesired,
                parameters.amountAMin,
                parameters.amountBMin,
                parameters.to,
                parameters.deadline
              ).estimateGas({ from: userAddress, gas: this.transaction.gasFee })
            await routerContract.methods
              .addLiquidity(
                parameters.tokenA,
                parameters.tokenB,
                parameters.amountADesired,
                parameters.amountBDesired,
                parameters.amountAMin,
                parameters.amountBMin,
                parameters.to,
                parameters.deadline
              ).send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then(async (result) => {
                this.transaction.loading = false
                this.transaction.pending = false
                this.transaction.tx = result.transactionHash
                const PeriodFactoryFile = await import('~/assets/abis/PeriodFactory.json')
                const PeriodFactory = PeriodFactoryFile.default
                const factoryContract = new window.eth.eth.Contract(PeriodFactory, this.$config.periodFactory)
                const pairAddress = await factoryContract.methods.getPair(this.pair.from_token.address, this.pair.to_token.address).call({ from: userAddress })
                this.$savePair(this.pair.from_token, this.pair.to_token, pairAddress)
                this.$saveTransaction({
                  tid0: this.pair.from_token.address,
                  tid1: this.pair.to_token.address,
                  tid0_token: this.pair.from_token,
                  tid1_token: this.pair.to_token,
                  t0_amount: this.pair.from_value,
                  t1_amount: this.pair.to_value,
                  hash: result.transactionHash,
                  gasUsed: result.gasUsed,
                  transactionIndex: result.transactionIndex,
                  type: 'add'
                },
                this.$t('common.supply_action') + ' ' + this.$formatCryptoAmount(parseFloat(this.pair.from_value), 4) + ' ' + this.pair.from_token.symbol +
                  ' ' + this.$t('common.and') + ' ' + this.$formatCryptoAmount(parseFloat(this.pair.to_value), 4) + ' ' + this.pair.to_token.symbol
                )
                await this.updatePoolShare(pairAddress)
                this.clearForm(false)
                if (this.$refs.supplyLiquidity !== undefined) {
                  this.changeModalGlobalState({
                    state: true
                  })
                  this.$refs.supplyLiquidity.initSuccessScreen()
                }
              })
              .catch((err) => {
                this.transaction.loading = false
                this.transaction.pending = false
                if (err.code === 4001) {
                  if (this.$refs.supplyLiquidity !== undefined) {
                    this.changeModalGlobalState({
                      state: true
                    })
                    this.$refs.supplyLiquidity.rejectTransaction({
                      type: 'denied'
                    })
                  }
                } else {
                  const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
                  if (this.$refs.supplyLiquidity !== undefined) {
                    this.changeModalGlobalState({
                      state: true
                    })
                    this.$refs.supplyLiquidity.rejectTransaction({
                      type: 'error',
                      message: errorMessage
                    })
                  }
                }
              })
          } catch (err) {
            this.transaction.loading = false
            this.transaction.pending = false
            let errorMessage = this.$t('liquidity.error_text')
            if (err.message !== undefined && err.message !== null) {
              errorMessage = this.$t('liquidity.error_text_with_message') + ': ' + this.$getRPCError(err.message)
            }
            if (this.$refs.supplyLiquidity !== undefined) {
              this.changeModalGlobalState({
                state: true
              })
              this.$refs.supplyLiquidity.rejectTransaction({
                type: 'error',
                message: errorMessage
              })
            }
          }
        } else {
          this.transaction.loading = false
          this.transaction.pending = false
        }
      }
    },
    async createPair() {
      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      const userAddress = await this.$getUserAddress()
      const PeriodFactoryFile = await import('~/assets/abis/PeriodFactory.json')
      const PeriodFactory = PeriodFactoryFile.default
      const factoryContract = new window.eth.eth.Contract(PeriodFactory, this.$config.periodFactory)
      let fromAddress = this.pair.from_token.address
      let toAddress = this.pair.to_token.address
      if (fromAddress === this.$config.ZERO || this.$isStableCurrency(this.pair.from_token.symbol)) {
        fromAddress = this.$config.wbnbAddress
      }
      if (toAddress === this.$config.ZERO || this.$isStableCurrency(this.pair.to_token.symbol)) {
        toAddress = this.$config.wbnbAddress
      }
      try {
        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await factoryContract.methods
          .createPair(
            fromAddress,
            toAddress
          ).estimateGas({ from: userAddress, gas: this.transaction.gasFee })

        let returnValue = false
        await factoryContract.methods
          .createPair(
            fromAddress,
            toAddress
          )
          .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            const pairAddress = result.events.PairCreated.returnValues.pair
            console.log(this.$t('liquidity.pair_created') + ': ' + pairAddress)
            returnValue = true
          })
          .catch((err) => {
            this.transaction.loading = false
            this.transaction.pending = false
            if (err.code === 4001) {
              if (this.$refs.supplyLiquidity !== undefined) {
                this.changeModalGlobalState({
                  state: true
                })
                this.$refs.supplyLiquidity.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.supplyLiquidity !== undefined) {
                this.changeModalGlobalState({
                  state: true
                })
                this.$refs.supplyLiquidity.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
            returnValue = false
          })

        return returnValue
      } catch (err) {
        this.transaction.loading = false
        this.transaction.pending = false
        let errorMessage = this.$t('liquidity.error_text')
        if (err.message !== undefined && err.message !== null) {
          errorMessage = this.$t('liquidity.error_text_with_message') + ': ' + this.$getRPCError(err.message)
        }
        if (this.$refs.supplyLiquidity !== undefined) {
          this.changeModalGlobalState({
            state: true
          })
          this.$refs.supplyLiquidity.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
        return false
      }
    },
    async checkAllowance(address, value) {
      try {
        const userAddress = await this.$getUserAddress()
        const ERC20file = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20file.default
        const erc20Contract = new window.eth.eth.Contract(ERC20, address)
        let returnValue = false
        const allowed = await erc20Contract.methods.allowance(userAddress, this.$config.periodRouter).call({ from: userAddress })
        const decimals = await erc20Contract.methods.decimals().call({ from: userAddress })
        const remainingAllowance = window.eth.utils.toBN(allowed)
        const serviceFeeWei = window.eth.utils.toBN(this.$toWeiDecimal(value, decimals))
        if (serviceFeeWei.gt(remainingAllowance)) {
          const gasPrice = await this.$calculateGasCost()
          const gasFeeEstimated = await erc20Contract.methods.approve(this.$config.periodRouter, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
            .estimateGas({ from: userAddress, gas: this.transaction.gasFee })

          await erc20Contract.methods.approve(this.$config.periodRouter, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
            .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
            .then((result) => {
              const emitedApproval = result.events.Approval.returnValues.value
              if (parseInt(emitedApproval, 10) > 0) {
                returnValue = true
              }
            })
            .catch((err) => {
              this.transaction.loading = false
              this.transaction.pending = false
              console.log(err)
              this.$denyWalletOperation()
              returnValue = false
            })
        } else {
          returnValue = true
        }
        return returnValue
      } catch (err) {
        console.log(err)
        this.transaction.loading = false
        this.transaction.pending = false
        this.$nuxt.$emit('openInfo', this.$t('swap.token_not_erc20'), this.$t('swap.token_not_erc20_text'))
        return false
      }
    },
    async addLiquidityETHParameters(tokenSide) {
      const userAddress = await this.$getUserAddress()
      const answer = {
        token: '',
        amountTokenDesired: '0',
        amountTokenMin: '0',
        amountETHMin: '0',
        to: '',
        deadline: 0,
        amountIn: '0'
      }
      if (tokenSide === 'from') {
        answer.token = this.pair.from_token.address
        answer.amountTokenDesired = this.$toWeiDecimal(this.pair.from_value, this.pair.from_token.decimals)
        answer.amountTokenMin = this.$toWeiDecimal(Number(this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
          this.pair.from_value / 100 * this.slippageTolerance).toString(), this.pair.from_token.decimals)
        answer.amountETHMin = this.$toWeiDecimal(Number(this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
          this.pair.to_value / 100 * this.slippageTolerance).toPrecision(10), this.pair.from_token.decimals)
        answer.to = userAddress
        answer.deadline = this.$calculateDeadline(this.deadline)
        answer.amountIn = window.eth.utils.toBN(this.$toWeiDecimal(Number(this.pair.to_value).toPrecision(10), this.pair.from_token.decimals))
      } else {
        answer.token = this.pair.to_token.address
        answer.amountTokenDesired = this.$toWeiDecimal(Number(this.pair.to_value).toPrecision(10), this.pair.to_token.decimals)
        answer.amountTokenMin = this.$toWeiDecimal(Number(this.pair.to_value - this.pair.to_value / 100 * this.liquidity.min_price_to -
          this.pair.to_value / 100 * this.slippageTolerance).toPrecision(10), this.pair.to_token.decimals)
        answer.amountETHMin = this.$toWeiDecimal(Number(this.pair.from_value - this.pair.from_value / 100 * this.liquidity.min_price_from -
          this.pair.from_value / 100 * this.slippageTolerance).toPrecision(10), this.pair.to_token.decimals)
        answer.to = userAddress
        answer.deadline = this.$calculateDeadline(this.deadline)
        answer.amountIn = window.eth.utils.toBN(this.$toWeiDecimal(Number(this.pair.from_value).toPrecision(10), this.pair.to_token.decimals))
      }
      return answer
    },
    async importAndSelectToken(address, type = 'from') {
      let addressCheck = address
      if (this.$isChecksumAddress(addressCheck)) {
        addressCheck = await window.eth.utils.toChecksumAddress(addressCheck)
      }
      const userAddress = await this.$getUserAddress()
      this.loading = true
      try {
        const ERC20file = await import('~/assets/abis/ERC20.json')
        const ERC20 = ERC20file.default
        const erc20Contract = new window.eth.eth.Contract(ERC20, addressCheck)
        const symbol = await erc20Contract.methods.symbol().call({ from: userAddress })
        const name = await erc20Contract.methods.name().call({ from: userAddress })
        const decimals = await erc20Contract.methods.decimals().call({ from: userAddress })
        const initBalance = await erc20Contract.methods.balanceOf(userAddress).call({ from: userAddress })
        const currentDecimal = this.$getDecimalsName(parseInt(decimals))
        let calcBalance = parseFloat(window.eth.utils.fromWei(initBalance, currentDecimal.name))
        if (currentDecimal.multiply > 0) {
          calcBalance /= 10 ** currentDecimal.multiply
        }
        const logoURI = this.$getImageByAddress(addressCheck)
        const newToken = {
          id: 'import',
          address: addressCheck,
          name,
          symbol,
          decimals,
          logoURI,
          balance: calcBalance
        }
        await this.addToken(newToken)
        if (type === 'from') {
          this.selectFromToken(newToken, false)
        } else {
          this.selectToToken(newToken, false)
        }
        this.loading = false
      } catch (err) {
        this.loading = false
        console.log(err)
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('swap.success')
          break
        case 'error':
          this.modalTitle = this.$t('swap.error_header')
          break
        default:
          this.modalTitle = this.$t('liquidity.you_will_receive')
      }
    },
    copyAddress(evt, address) {
      this.$copy(evt, address)
      this.copyText = this.$t('common.copied')
      setTimeout(() => {
        this.copyText = this.$t('common.copy')
      }, 1200)
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    },
    getLabelByFee(fee) {
      switch (fee) {
        case 0.05:
          return this.$t('liquidity.stable_pairs')
        case 0.3:
          return this.$t('liquidity.most_pairs')
        case 1:
          return this.$t('liquidity.exotic_pairs')
        default:
          return this.$t('liquidity.most_pairs')
      }
    },
    toggleModals(evt) {
      if (
        this.$refs.settingsButton !== undefined &&
        this.$refs.settingsArea !== undefined &&
        !this.$refs.settingsArea.$el.contains(evt.target) &&
        !this.$refs.settingsButton.contains(evt.target) &&
        this.$refs.settingsButton.classList.contains('active')
      ) {
        this.toggleSettings()
      }
      if (
        this.$refs.fromListButton !== undefined &&
        this.$refs.fromList !== undefined &&
        !this.$refs.fromList.contains(evt.target) &&
        !this.$refs.fromListButton.contains(evt.target) &&
        !this.$refs.manageFromArea.$el.contains(evt.target) &&
        !this.$refs.manageFromButton.contains(evt.target) &&
        this.$refs.fromList.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleFromList()
      }
      if (
        this.$refs.manageFromButton !== undefined &&
        this.$refs.manageFromArea !== undefined &&
        !this.$refs.manageFromArea.$el.contains(evt.target) &&
        !this.$refs.manageFromButton.contains(evt.target) &&
        this.$refs.manageFromArea.$el.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleManageFrom()
      }
      if (
        this.$refs.toListButton !== undefined &&
        this.$refs.toList !== undefined &&
        !this.$refs.toList.contains(evt.target) &&
        !this.$refs.toListButton.contains(evt.target) &&
        !this.$refs.manageToArea.$el.contains(evt.target) &&
        !this.$refs.manageToButton.contains(evt.target) &&
        this.$refs.toList.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleToList()
      }
      if (
        this.$refs.manageToButton !== undefined &&
        this.$refs.manageToArea !== undefined &&
        !this.$refs.manageToArea.$el.contains(evt.target) &&
        !this.$refs.manageToButton.contains(evt.target) &&
        this.$refs.manageToArea.$el.classList.contains('active') &&
        !evt.target.classList.contains('remove-list')
      ) {
        this.toggleManageTo()
      }
    },
    async updatePoolShare(pairAddress) {
      try {
        const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
        const PeriodPair = PeriodPairFile.default
        const pairContract = new window.eth.eth.Contract(PeriodPair, pairAddress)
        const balance = await pairContract.methods.balanceOf(this.user.address).call({ from: this.user.address })
        const totalSupply = await pairContract.methods.totalSupply().call({ from: this.user.address })
        const reservres = await pairContract.methods.getReserves().call({ from: this.user.address })
        this.updatePairShare({
          chainId: this.$config.chainId,
          address: pairAddress,
          share: balance / totalSupply * 100,
          totalSupply: parseFloat(window.eth.utils.fromWei(totalSupply)),
          reservres,
          tid0: this.pair.from_token.address,
          tid1: this.pair.to_token.address,
          t0_amount: this.pair.from_value,
          t1_amount: this.pair.to_value
        })
      } catch (err) {
        console.error(err)
      }
    }
  }
}
</script>

<style scoped>
  @import url('~/assets/css/new/add-liquidity.css');
  @import url('~/assets/css/new/tokenSelector.css');
</style>
